import { useEffect, useRef } from "react";
import camelcaseKeys from "camelcase-keys";
import { useNavigate } from "react-router-dom";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import config from "config";
import { AXIOS } from "hooks/axios";
import { useError } from "components/status/ErrorProvider";
import { useSuccess } from "components/status/SuccessProvider";
import { INIT_USER, useAuth, User } from "components/auth/AuthProvider";
import { MetaSchema, PaginationSchema } from "hooks/pagination";

const USER_SERVICE_BASE_URL = config.url.USER_SERVICE;

type LocalSignup = {
  email: string;
  password: string;
  confirmation: string;
  lastName: string;
  firstName: string;
};
export const useUserServiceLocalSignup = () => {
  const { setError } = useError();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    LocalSignup
  >(
    [`USER SERVICE LOCAL SIGNUP`],
    (props: LocalSignup) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/local/signup`, props),
    {
      onSuccess: (data) => {
        const authCodeUrl = data.data?.url;
        if (authCodeUrl) window.location.href = authCodeUrl;
      },
      onError: (error) => {
        const message = error?.response?.data?.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type LocalSignin = {
  email: string;
  password: string;
};
export const useUserServiceLocalSignin = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { setError } = useError();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    LocalSignin
  >(
    [`USER SERVICE LOCAL SIGNIN`],
    (props: LocalSignin) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/local/signin`, props),
    {
      onSuccess: (data) => {
        const user = camelcaseKeys(data.data.user, { deep: true });

        // set local user
        auth.setAuth(true);
        auth.setUser(user);
        localStorage.setItem("GRACE_AUTHENTICATED", "true");

        navigate("/");
      },
      onError: (error) => {
        const message = error?.response?.data?.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
    }
  );
  return mutation;
};

export const useUserServiceSignIn = () => {
  return useMutation(
    [`USER SERVICE SIGN IN`],
    () => AXIOS.get(`${USER_SERVICE_BASE_URL}/auth/signin`),
    {
      onSuccess: (data) => {
        const authCodeUrl = data.data?.url;

        if (authCodeUrl) window.location.href = authCodeUrl;
      },
      onError: (error) => {
        console.log(error);
      },
    }
  );
};

export const useUserServiceUsers = (body?: PaginationSchema) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    { data: User[]; meta: MetaSchema }
  >(
    [`USER SERVICE USERS ${JSON.stringify(body)}`],
    () => AXIOS.post(`${USER_SERVICE_BASE_URL}/users`, body),
    {
      select: (data) => camelcaseKeys(data.data, { deep: true }),
    }
  );
};

export const useUserServiceSession = () => {
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  const session = useMutation(
    [`USER SERVICE SESSION`],
    () => AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/session`),
    {
      onSuccess: (data) => {
        // ping /session route every N minutes (configurable)
        if (!intervalRef.current) {
          // console.log("starting /session loop");
          intervalRef.current = setInterval(() => {
            session.mutate();
          }, config.session.INTERVAL);
        }
      },
      onError: () => {
        // clear interval on error to prevent retry loops
        if (intervalRef.current) {
          // console.log("clearing /session loop");
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      },
    }
  );

  // clean up interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return session;
};

export const useUserServiceSignOut = () => {
  const qc = useQueryClient();
  const auth = useAuth();
  const navigate = useNavigate();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>
  >(
    [`USER SERVICE SIGN OUT`],
    () => AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/signout`),
    {
      onSuccess: (data) => {
        // remove redirect url from local storage
        localStorage.removeItem("GRACE_REDIRECT_URL");
        localStorage.removeItem("GRACE_AUTHENTICATED");

        // clear react query cache
        qc.clear();

        // clear local auth/user
        auth.setAuth(false);
        auth.setUser(INIT_USER);
        setSuccess("Signed out");

        navigate("/signin");
      },
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
    }
  );

  return mutation;
};

type UserUpdate = {
  lastName: string | undefined;
  firstName: string | undefined;
};
export const useUserServiceUpdateProfile = () => {
  const { setUser } = useAuth();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    UserUpdate
  >(
    [`USER SERVICE UPDATE PROFILE`],
    (props: UserUpdate) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/update/profile`, props),
    {
      onSuccess: (data) => {
        const user = camelcaseKeys(data.data.user, { deep: true });

        // add user to local state
        setUser(user);

        setSuccess("Profile updated");
      },
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      cacheTime: Infinity,
    }
  );

  return mutation;
};

type RoleUpdate = {
  id: string;
  role: User["role"];
};
export const useUserServiceUpdateRole = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    RoleUpdate
  >(
    [`USER SERVICE UPDATE ROLE`],
    (props: RoleUpdate) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/update/role`, props),
    {
      cacheTime: Infinity,
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: () => {
        setSuccess("Role updated");

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });
      },
    }
  );

  return mutation;
};

type AccessUpdate = {
  id: string;
  status: User["status"];
};
export const useUserServiceUpdateAccess = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    AccessUpdate
  >(
    [`USER SERVICE UPDATE ACCESS`],
    (props: AccessUpdate) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/auth/update/access`, props),
    {
      cacheTime: Infinity,
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: () => {
        setSuccess("Access updated");

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });
      },
    }
  );

  return mutation;
};

export interface RequestSchema {
  id: string;
  uri: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  subject: string;
  project: string;
  approver: string;
  requester: string;
  updatedAt: Date;
}

export const useUserServiceRequests = (body: {
  status?: "PENDING" | "CLOSED";
}) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    { data: RequestSchema[]; meta: MetaSchema }
  >(
    [`USER SERVICE REQUESTS ${JSON.stringify(body)}`],
    () => AXIOS.post(`${USER_SERVICE_BASE_URL}/requests`, body),
    {
      retry: 2,
      select: (data) => camelcaseKeys(data.data, { deep: true }),
      staleTime: 1000 * 120,
      refetchOnMount: false,
    }
  );
};

export const useUserServiceRequestDetail = (body: { id: string }) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    { data: RequestSchema }
  >(
    [`USER SERVICE REQUESTS DETAIL ${JSON.stringify(body)}`],
    () => AXIOS.post(`${USER_SERVICE_BASE_URL}/requests/detail`, body),
    {
      retry: 2,
      select: (data) => camelcaseKeys(data.data, { deep: true }),
    }
  );
};

// note: fwupdate/release responsibility given to pavm service

type Release = {
  size: number;
  updatedAt: string;
  os: {
    name: string;
    version: string;
  };
  release: {
    name: string;
    version: string;
  };
};
export const useUserServiceReleases = () => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    Release[]
  >(
    [`USER SERVICE RELEASES`],
    () => AXIOS.get(`${USER_SERVICE_BASE_URL}/releases`),
    {
      retry: 2,
      staleTime: 120000,
      select: (data) => camelcaseKeys(data.data.data, { deep: true }),
    }
  );
};

type ReleaseUpload = {
  os: string;
  osVersion: string;
  releaseName: string;
  releaseVersion: string;
  file: any;
};
export const useUserServiceReleasesUpload = () => {
  const { setError } = useError();
  const releases = useUserServiceReleases();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ReleaseUpload
  >(
    [`USER SERVICE RELEASES UPLOAD`],
    (props: ReleaseUpload) => {
      console.log("props", props);

      return AXIOS.post(
        `${USER_SERVICE_BASE_URL}/releases/upload/${props.os}/${props.osVersion}/${props.releaseName}/${props.releaseVersion}`,
        { file: props.file },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },
    {
      retry: 2,
      onSuccess: (data) => {
        // refetch releases
        releases.refetch();

        return camelcaseKeys(data.data.data, { deep: true });
      },
      onError: (err, query) => {
        const message = err?.response?.data?.message ?? "";
        setError({ message, reset: () => mutation.reset });

        return false;
      },
    }
  );

  return mutation;
};
